import { useEffect, useMemo, useState } from "react";
import { FileText, Clock, CheckCircle2, Banknote, XCircle } from "lucide-react";
import { http, money, notify } from "../api";

/**
 * Login Status monthly KPI cards + month picker.
 * Cards are clickable — clicking sets `activeBucket` and the caller can use
 * `mapBucketToStatus()` to translate to a lead-status filter for the table below.
 */
const CARDS = [
  { key: "login",     label: "Login Files",   icon: FileText,     color: "var(--red)" },
  { key: "pending",   label: "Login Pending", icon: Clock,        color: "#c97d16" },
  { key: "approved",  label: "Approved",      icon: CheckCircle2, color: "#2f7d32" },
  { key: "disbursed", label: "Disbursed",     icon: Banknote,     color: "#1c6b8f" },
  { key: "rejected",  label: "Rejected",      icon: XCircle,      color: "#a01e1e" },
];

// Bucket → lead-status filter applied to the existing /api/leads list.
// `pending` uses a "not any of these" filter so the underlying leads table
// mirrors the same "everything except terminal buckets" logic as the KPI card.
export const mapBucketToStatus = {
  login: "Login Done",
  // Sentinel string — the /leads endpoint expands this to
  // {status: {$nin: ["Login Done","Approved","Disbursed","Rejected"]}}.
  pending: "__login_pending__",
  approved: "Approved",
  disbursed: "Disbursed",
  rejected: "Rejected",
};

function monthOptions() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-IN", { month: "long", year: "numeric" });
    out.push({ key, label });
  }
  return out;
}

export function LoginStatusSummary({ activeBucket, onBucketChange, user }) {
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState({ rows: null, loading: false });

  // -------- Dropdown filters (reuses Calling Performance scope endpoint) -----
  const role = user?.role;
  const isManager = role === "sales_manager";
  const isAdmin = role === "admin" || role === "business_head" || role === "sub_admin";
  const showFilters = isManager || isAdmin;
  const [scope, setScope] = useState(null); // { teams, sales_associates }
  const [teamId, setTeamId] = useState("");
  const [saId, setSaId] = useState("");
  useEffect(() => {
    if (!showFilters) return;
    http.get("/sa/performance-scope")
      .then((r) => setScope(r.data))
      .catch(() => setScope({ teams: [], sales_associates: [] }));
  }, [showFilters]);
  const saOptions = useMemo(() => {
    if (!scope) return [];
    if (isManager) return scope.sales_associates;
    if (!teamId) return scope.sales_associates;
    return scope.sales_associates.filter((s) => s.team_id === teamId);
  }, [scope, teamId, isManager]);
  useEffect(() => {
    if (saId && !saOptions.find((s) => s.id === saId)) setSaId("");
  }, [saOptions, saId]);

  const scopeQs = () => {
    const qs = new URLSearchParams();
    if (saId) qs.set("sa_id", saId);
    else if (teamId) qs.set("team_id", teamId);
    return qs.toString();
  };

  useEffect(() => {
    setLoading(true);
    const extra = scopeQs();
    http
      .get(`/reports/login-status?month=${month}${extra ? `&${extra}` : ""}`)
      .then((r) => setData(r.data))
      .catch((err) => notify.error(err, "Could not load Login Status summary"))
      .finally(() => setLoading(false));
  }, [month, saId, teamId]);

  // Drill-down: when a card is picked, fetch that bucket's file list for the month.
  useEffect(() => {
    if (!activeBucket) { setDrill({ rows: null, loading: false }); return; }
    setDrill({ rows: null, loading: true });
    const extra = scopeQs();
    http
      .get(`/reports/login-status/list?bucket=${activeBucket}&month=${month}${extra ? `&${extra}` : ""}`)
      .then((r) => setDrill({ rows: r.data.applications || [], loading: false }))
      .catch((err) => {
        setDrill({ rows: [], loading: false });
        notify.error(err, "Could not load file list");
      });
  }, [activeBucket, month, saId, teamId]);

  const activeCard = CARDS.find((c) => c.key === activeBucket);

  return (
    <section
      data-testid="login-status-summary"
      style={{ margin: "0 0 18px" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <p className="eyebrow" style={{ margin: 0 }}>MONTHLY SUMMARY</p>
          <p className="muted" style={{ fontSize: 13, margin: "2px 0 0" }}>
            Actual counts and amounts from your Login files. Tap a card to see the underlying files.
          </p>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <span className="muted" style={{ fontSize: 12 }}>Month</span>
          <select
            data-testid="login-status-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ padding: "8px 12px", minHeight: 40 }}
          >
            {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>
        {showFilters && scope && (
          <>
            {isAdmin && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <span className="muted" style={{ fontSize: 12 }}>Manager / Team</span>
                <select
                  data-testid="login-status-team-select"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  style={{ padding: "8px 12px", minHeight: 40 }}
                >
                  <option value="">All Teams</option>
                  {(scope.teams || []).map((t) => (
                    <option key={t.team_id} value={t.team_id}>{t.name}</option>
                  ))}
                </select>
              </label>
            )}
            {(isManager || teamId) && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <span className="muted" style={{ fontSize: 12 }}>{isManager ? "Team Member" : "Sales Associate"}</span>
                <select
                  data-testid="login-status-sa-select"
                  value={saId}
                  onChange={(e) => setSaId(e.target.value)}
                  style={{ padding: "8px 12px", minHeight: 40 }}
                >
                  <option value="">All Sales Associates</option>
                  {saOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.employee_code ? ` · ${s.employee_code}` : ""}</option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}
      </div>

      <div
        data-testid="login-status-cards"
        className="login-status-grid"
      >
        {CARDS.map((c) => {
          const b = data?.[c.key] || { files: 0, amount: 0 };
          const active = activeBucket === c.key;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`login-status-card-${c.key}`}
              className={`login-status-card ${active ? "active" : ""}`}
              onClick={() => onBucketChange(active ? "" : c.key)}
              style={{ borderLeftColor: c.color }}
            >
              <div className="lsc-head">
                <Icon size={16} style={{ color: c.color }} />
                <span>{c.label}</span>
              </div>
              <div className="lsc-count" data-testid={`login-status-count-${c.key}`}>
                {loading ? "…" : b.files}
                <small>files</small>
              </div>
              <div className="lsc-amount" data-testid={`login-status-amount-${c.key}`}>
                {loading ? "…" : money(b.amount)}
              </div>
            </button>
          );
        })}
      </div>

      {activeCard && (
        <div
          className="login-status-drill"
          data-testid={`login-status-drill-${activeCard.key}`}
        >
          <div className="lsd-head">
            <div>
              <p className="eyebrow" style={{ margin: 0, color: activeCard.color }}>{activeCard.label.toUpperCase()}</p>
              <p className="muted" style={{ fontSize: 12, margin: "2px 0 0" }}>
                {drill.rows ? `${drill.rows.length} file${drill.rows.length === 1 ? "" : "s"} in this bucket` : "Loading…"}
              </p>
            </div>
            <button
              type="button"
              className="outline-button compact"
              data-testid="login-status-drill-close"
              onClick={() => onBucketChange("")}
              style={{ fontSize: 12 }}
            >
              Close drill-down
            </button>
          </div>

          {drill.loading && <p className="muted" style={{ padding: 12 }}>Loading files…</p>}

          {!drill.loading && drill.rows && drill.rows.length === 0 && (
            <p className="muted" data-testid="login-status-drill-empty" style={{ padding: 12 }}>
              No files in this bucket for the selected month.
            </p>
          )}

          {!drill.loading && drill.rows && drill.rows.length > 0 && (
            <div className="table-wrap" data-testid="login-status-drill-table">
              <table>
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Bank · Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.rows.map((a) => {
                    const amount = activeCard.key === "approved" ? a.approved_amount
                                 : activeCard.key === "disbursed" ? a.disbursed_amount
                                 : a.requested_amount;
                    return (
                      <tr key={a.application_id} data-testid={`login-status-drill-row-${a.application_id}`}>
                        <td className="mono" style={{ fontSize: 12 }}>{a.application_id}</td>
                        <td><strong>{a.customer_name || "—"}</strong></td>
                        <td className="mono" style={{ fontSize: 12 }}>{a.mobile || "—"}</td>
                        <td>
                          {a.bank_name || "—"}
                          {a.product_name ? <div className="muted" style={{ fontSize: 11 }}>{a.product_name}</div> : null}
                        </td>
                        <td>{money(amount || 0)}</td>
                        <td><span className="chip chip-muted">{a.status}</span></td>
                        <td>
                          {a.assigned_name || "—"}
                          {a.sales_manager_name ? <div className="muted" style={{ fontSize: 11 }}>{a.sales_manager_name}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
